import React, { useState } from "react";
import { Box, Stack } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import TextInput from "qubCommon/Inputs/TextInput";
import { changeEmail, verifyEmail } from "apis/controllers/users";
import { enqueueSnackbar } from "notistack";
import { logout } from "store/authReducer";
import { useDispatch } from "react-redux";
import { persistor } from "store/store";

const ChangeUsername: React.FC = () => {
    const dispatch = useDispatch();

    const [step, setStep] = useState<1 | 2>(1);
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);

    const schema = yup.object({
        newEmail: yup
            .string()
            .min(4, "Τουλάχιστον 4 χαρακτήρες")
            .email("To username πρέπει να αντιστοιχεί σε email.")
            .required("Απαιτείται username"),

        verificationPin: yup.string().when([], {
            is: () => step === 2,
            then: (schema) =>
                schema
                    .length(6, "Ο κωδικός πρέπει να έχει 6 ψηφία")
                    .required("Απαιτείται ο κωδικός επιβεβαίωσης"),
            otherwise: (schema) => schema.notRequired(),
        }),
    });

    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            newEmail: "",
            verificationPin: "",
        },
    });

    const { handleSubmit } = methods;

    const onSubmit = async (data: any) => {
        if (loading) return;
        setLoading(true);

        try {
            // STEP 1 → Στέλνουμε email (αποστολή κωδικού)
            if (step === 1) {
                const res = await changeEmail({ newEmail: data.newEmail });

                if (res?.status === "success") {
                    enqueueSnackbar(
                        "Στάλθηκε κωδικός επιβεβαίωσης στο email σας",
                        { variant: "info" }
                    );
                    setStep(2);
                } else {
                    enqueueSnackbar(res?.message || "Σφάλμα", { variant: "error" });
                }
            }

            // STEP 2 → Επιβεβαίωση με PIN
            if (step === 2) {
                const res = await verifyEmail({
                    newEmail: data.newEmail,
                    code: data.verificationPin,
                });

                if (res?.status === "success") {
                    enqueueSnackbar(
                        res?.message || "Το username άλλαξε με επιτυχία",
                        { variant: "success" }
                    );
                    setStep(1);
                    methods.reset();
                    dispatch(logout());
                    persistor.purge();
                } else {
                    enqueueSnackbar(res?.message || "Λάθος PIN", { variant: "error" });
                }
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar("Κάτι πήγε στραβά", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleResendCode = async () => {
        // Ξαναστέλνουμε τον ίδιο κωδικό στο ίδιο email
        const { newEmail } = methods.getValues();

        if (!newEmail) {
            enqueueSnackbar("Συμπληρώστε πρώτα το νέο username (email)", {
                variant: "warning",
            });
            return;
        }

        if (resendLoading) return;
        setResendLoading(true);

        try {
            const res = await changeEmail({ newEmail });

            if (res?.status === "success") {
                enqueueSnackbar(
                    "Ο κωδικός επιβεβαίωσης στάλθηκε ξανά στο email σας",
                    { variant: "info" }
                );
            } else {
                enqueueSnackbar(res?.message || "Σφάλμα κατά την επαναποστολή", {
                    variant: "error",
                });
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar("Κάτι πήγε στραβά κατά την επαναποστολή", {
                variant: "error",
            });
        } finally {
            setResendLoading(false);
        }
    };

    return (
        <FormProvider {...methods}>
            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={2}>
                    <TextInput name="newEmail" label="Νέο Username" isFullWidth />

                    {step === 2 && (
                        <>
                            <TextInput
                                name="verificationPin"
                                label="Κωδικός Επιβεβαίωσης (PIN)"
                                isFullWidth
                            />


                        </>
                    )}

                    <LoadingButton loading={loading} type="submit" variant="contained">
                        {step === 1 ? "Αποστολή Κωδικού" : "Επιβεβαίωση Αλλαγής"}
                    </LoadingButton>
                    {step === 2 && <LoadingButton
                        onClick={handleResendCode}
                        loading={resendLoading}
                        variant="contained"
                        color='secondary'
                        sx={{ color: "#fefefe" }}
                    >
                        Επαναποστολή κωδικού
                    </LoadingButton>}
                </Stack>
            </Box>
        </FormProvider>
    );
};

export default ChangeUsername;
